const { Matrix, SingularValueDecomposition } = require('ml-matrix');

type Matrix2d = number[][];

const getRandomIndices = (length: number, numIndices: number) => {
  const indices = Array.from(Array(length), (_, index) => index);
  const randomIndices = [];

  for (let i = 0; i < numIndices; i++) {
    const randomIndex = Math.floor(Math.random() * indices.length);
    randomIndices.push(indices[randomIndex]);
    indices.splice(randomIndex, 1);
  }

  return randomIndices;
};

const computeHomographyMatrixFromPoints = (srcPoints: Matrix2d, dstPoints: Matrix2d): Matrix2d => {
  if (srcPoints.length !== dstPoints.length || srcPoints.length < 4) {
    throw new Error('Invalid number of points');
  }

  const numPoints = srcPoints.length;

  const A = new Matrix(numPoints * 2, 9);

  for (let i = 0; i < numPoints; i++) {
    const [srcX, srcY] = srcPoints[i];
    const [dstX, dstY] = dstPoints[i];

    const rowIndex = i * 2;
    A.set(rowIndex, 0, srcX);
    A.set(rowIndex, 1, srcY);
    A.set(rowIndex, 2, 1);
    A.set(rowIndex, 6, -srcX * dstX);
    A.set(rowIndex, 7, -srcY * dstX);
    A.set(rowIndex, 8, -dstX);

    A.set(rowIndex + 1, 3, srcX);
    A.set(rowIndex + 1, 4, srcY);
    A.set(rowIndex + 1, 5, 1);
    A.set(rowIndex + 1, 6, -srcX * dstY);
    A.set(rowIndex + 1, 7, -srcY * dstY);
    A.set(rowIndex + 1, 8, -dstY);
  }

  const svd = new SingularValueDecomposition(A);

  // check if the matrix is singular
  if (svd.rank !== numPoints * 2) {
    throw new Error('Matrix is singular');
  }

  const V = svd.rightSingularVectors;

  const homographyMatrix: Matrix2d = [];
  for (let i = 0; i < 3; i++) {
    homographyMatrix.push([]);
    for (let j = 0; j < 3; j++) {
      homographyMatrix[i].push(V.get(i * 3 + j, V.columns - 1));
    }
  }

  // Normalize the homography matrix
  const norm = homographyMatrix[2][2];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      homographyMatrix[i][j] /= norm;
    }
  }

  return homographyMatrix;
};

export const computeHomographyMatrix = (srcPoints: Matrix2d, dstPoints: Matrix2d) => {
  if (srcPoints.length !== dstPoints.length || srcPoints.length < 4) {
    throw new Error('Invalid number of points');
  }

  const numIterations = 1000; // Number of RANSAC iterations
  const inlierThreshold = 5; // Inlier threshold distance

  let bestHomographyMatrix = null;
  let bestInlierCount = 0;

  for (let iteration = 0; iteration < numIterations; iteration++) {
    // Randomly select 4 point correspondences
    const randomIndices = getRandomIndices(srcPoints.length, 4);
    const sampledSrcPoints = randomIndices.map((index) => srcPoints[index]);
    const sampledDstPoints = randomIndices.map((index) => dstPoints[index]);

    // Compute the homography matrix using the sampled points
    let homographyMatrix;
    try {
      homographyMatrix = computeHomographyMatrixFromPoints(sampledSrcPoints, sampledDstPoints);
    } catch (error) {
      continue; // Skip this iteration if homography calculation fails
    }

    // Count inliers
    let inlierCount = 0;

    // Compute the Euclidean distance between the transformed source point and the destination point
    const distances = calculateReprojectionErrors(srcPoints, dstPoints, homographyMatrix);

    for (let i = 0; i < distances.length; i++) {
      if (distances[i] < inlierThreshold) {
        inlierCount++;
      }
    }

    // Check if the current model is the best so far
    if (inlierCount > bestInlierCount) {
      bestInlierCount = inlierCount;
      bestHomographyMatrix = homographyMatrix;
    }
  }

  return bestHomographyMatrix;
};

const calculateReprojectionErrors = (srcPoints: Matrix2d, dstPoints: Matrix2d, homographyMatrix: Matrix2d): number[] => {
  // Ensure both sets of points have the same number of points
  if (srcPoints.length !== dstPoints.length) {
    throw new Error('Number of source points and destination points must be the same.');
  }

  const numPoints = srcPoints.length;
  const reprojectionErrors = [];

  for (let i = 0; i < numPoints; i++) {
    const srcPoint = srcPoints[i];
    const dstPoint = dstPoints[i];

    // Transform the source point using the homography matrix
    const transformedSrcPoint = applyHomography(homographyMatrix, srcPoint);

    // Compute the Euclidean distance between the transformed source point and the destination point
    const distance = Math.sqrt((transformedSrcPoint[0] - dstPoint[0]) ** 2 + (transformedSrcPoint[1] - dstPoint[1]) ** 2);
    reprojectionErrors.push(distance);
  }

  return reprojectionErrors;
};

const applyHomography = (homography: Matrix2d, point: number[]) => {
  const [x, y] = point;

  const transformedX =
    (homography[0][0] * x + homography[0][1] * y + homography[0][2]) / (homography[2][0] * x + homography[2][1] * y + homography[2][2]);
  const transformedY =
    (homography[1][0] * x + homography[1][1] * y + homography[1][2]) / (homography[2][0] * x + homography[2][1] * y + homography[2][2]);

  return [transformedX, transformedY];
};
